import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Chip, Stack, TextField, Typography } from "@mui/material";
import { Link as RouterLink, useParams } from "react-router-dom";
import api from "../bootstrapApi";
import { addCartItem, useShopCart } from "../shopCart";

const sizeOptions = ["26", "28", "30", "32", "34"];

export default function ShopProductPage() {
  const accent = "#e86026";
  const accentSoft = "rgba(232,96,38,0.16)";
  const support = "#656e8f";
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [message, setMessage] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState("30");
  const cartItems = useShopCart();

  useEffect(() => {
    api
      .get(`/api/public/products/${productId}`)
      .then((response) => {
        setProduct(response.data);
        setMessage(null);
      })
      .catch(() => setMessage({ type: "error", text: "Le produit n'a pas pu etre charge." }));
  }, [productId]);

  const cartCount = useMemo(
    () => cartItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0),
    [cartItems]
  );

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "1.02fr 0.98fr" }, gap: 3 }}>
      <Box
        sx={{
          borderRadius: "26px",
          overflow: "hidden",
          border: "1px solid #f0ddd3",
          background: "#fff",
          boxShadow: "0 18px 38px rgba(101,110,143,0.08)",
        }}
      >
        <Box
          sx={{
            p: { xs: 2.2, md: 2.8 },
            minHeight: 520,
            background:
              "radial-gradient(circle at center, rgba(232,96,38,0.14) 0%, rgba(232,96,38,0.06) 18%, transparent 19%), linear-gradient(180deg, #fff8f3 0%, #f6efea 100%)",
            display: "grid",
            alignContent: "start",
            gap: 2,
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Button
              component={RouterLink}
              to={product ? `/shop/category/${product.category_id}` : "/shop"}
              variant="text"
              sx={{ color: support, fontWeight: 700 }}
            >
              Retour
            </Button>
            <Chip label={`Korb ${cartCount}`} sx={{ borderRadius: "999px", fontWeight: 700, bgcolor: "#ffffff" }} />
          </Stack>

          <Box
            sx={{
              position: "relative",
              display: "grid",
              placeItems: "center",
              minHeight: 320,
            }}
          >
            <Box
              sx={{
                position: "absolute",
                width: { xs: 240, md: 330 },
                height: { xs: 120, md: 160 },
                borderRadius: "50%",
                border: `2px solid ${accentSoft}`,
                transform: "translateY(48px)",
              }}
            />
            <Box
              sx={{
                width: "100%",
                maxWidth: 420,
                aspectRatio: "1 / 1",
                backgroundImage: product?.primary_image
                  ? `url(${product.primary_image})`
                  : "linear-gradient(135deg, rgba(23,39,61,0.12), rgba(255,183,3,0.18))",
                backgroundSize: "contain",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
              }}
            />
          </Box>

          <Stack direction="row" spacing={1.2} justifyContent="center">
            {[1, 2, 3].map((index) => (
              <Box
                key={index}
                sx={{
                  width: 76,
                  height: 60,
                  borderRadius: "16px",
                  border: index === 1 ? `2px solid ${accent}` : "1px solid #f0ddd3",
                  backgroundImage: product?.primary_image
                    ? `url(${product.primary_image})`
                    : "linear-gradient(135deg, rgba(23,39,61,0.12), rgba(255,183,3,0.18))",
                  backgroundSize: "contain",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "center",
                  bgcolor: "#fff",
                }}
              />
            ))}
          </Stack>
        </Box>
      </Box>

      <Box
        sx={{
          borderRadius: "26px",
          border: "1px solid #f0ddd3",
          background: "#fff",
          boxShadow: "0 18px 38px rgba(101,110,143,0.08)",
          p: 3,
          display: "grid",
          gap: 1.7,
          alignContent: "start",
        }}
      >
        <Typography variant="overline" sx={{ color: "#7b8aa3", letterSpacing: "0.12em" }}>
          {product?.category_name || "PRODUIT"}
        </Typography>
        <Typography variant="h4" sx={{ color: "#1f2d48", fontWeight: 800 }}>
          {product?.name || "Produit"}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip label={product?.product_type === "custom" ? "Sur mesure" : "Produit standard"} />
          <Chip label={`Panier ${cartCount}`} variant="outlined" />
        </Stack>
        <Typography variant="body1" color="text.secondary">
          {product?.description || "Description du produit en cours de chargement."}
        </Typography>

        <Box>
          <Typography variant="h5" sx={{ color: accent, fontWeight: 800 }}>
            {product ? `${Number(product.price || 0).toFixed(2)} ${product.currency || "EUR"}` : "-"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Ajoutez votre produit puis finalisez une seule demande dans le panier.
          </Typography>
        </Box>

        {message ? <Alert severity={message.type}>{message.text}</Alert> : null}

        <Box sx={{ display: "grid", gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: "#33415c" }}>
            Taille
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {sizeOptions.map((size) => (
              <Chip
                key={size}
                label={size}
                color={selectedSize === size ? "primary" : "default"}
                onClick={() => setSelectedSize(size)}
                sx={{ borderRadius: "12px", minWidth: 48 }}
              />
            ))}
          </Stack>
        </Box>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
          <TextField
            label="Quantite"
            type="number"
            value={quantity}
            onChange={(event) => setQuantity(Math.max(1, Number(event.target.value || 1)))}
            sx={{ maxWidth: 140 }}
          />
          <Button
            variant="contained"
            sx={{ bgcolor: accent, color: "#fff", fontWeight: 800, "&:hover": { bgcolor: "#c94b19" } }}
            onClick={() => {
              if (!product) {
                return;
              }
              addCartItem({
                productId: product.id,
                name: product.name,
                price: product.price,
                currency: product.currency,
                imageUrl: product.primary_image,
                categoryName: product.category_name,
                productType: product.product_type,
                quantity,
                notes: `Groesse ${selectedSize}`,
              });
              setMessage({ type: "success", text: "Produit ajoute au panier." });
            }}
          >
            Ajouter au panier
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
